import {
  withCompInfo,
  createComponentMapperModel,
  withUiTypeCompInfo,
} from '@wix/editor-elements-integrations';
import {
  withFallbackValues,
  castToInheritIfNeeded,
  castFromInheritIfNeeded,
  getOppositeAlign,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';
import type {
  SignatureInputDefinition,
  ISignatureInputMapperProps,
  SignatureInputStyleProperties,
  SignatureInputLabelPaddings,
  SignatureInputCSSVars,
  SignatureInputCssCarmiData,
  TypeOrDrawSignatureInputUiType,
} from '../SignatureInput.types';
import { isTypeOrDrawSkin } from './utils';
import { TranslationKeys, TranslationNamespace } from '../constants';

const isThemeColor = (color: string) => /^color_\d+$/.test(color);
const getFromColorMap = (rawColor: string, colorsMap: Array<string>) => {
  const [, themeColorIndex] = rawColor.split('_');
  const i = parseInt(themeColorIndex, 10);
  return typeof i === 'number' && isNaN(i) === false ? colorsMap[i] : rawColor;
};

const getSignatureColorFromStyleProperties = (
  styleProperties: Record<string, string>,
  siteColors: Array<string>,
) => {
  const { signatureColor } = styleProperties;
  return isThemeColor(signatureColor)
    ? getFromColorMap(signatureColor, siteColors)
    : signatureColor;
};

export const props = withCompInfo<
  ISignatureInputMapperProps,
  SignatureInputDefinition
>()(
  ['compData', 'compProps', 'translate', 'styleProperties', 'siteColors'],
  ({ compData, compProps, styleProperties, siteColors, translate }) => {
    const { signatureSize } = styleProperties;

    return {
      label: compData.titleText,
      clearButtonText: compData.clearButtonText,
      required: compProps.required || false,
      isDisabled: compProps.isDisabled || false,
      signatureSize: signatureSize?.toString()?.endsWith('px')
        ? signatureSize.slice(0, -2)
        : signatureSize,
      signatureColor: getSignatureColorFromStyleProperties(
        styleProperties,
        siteColors,
      ),
      translations: {
        radioTypeLabel: translate(
          TranslationNamespace,
          TranslationKeys.radioTypeLabel,
        ),
        radioDrawLabel: translate(
          TranslationNamespace,
          TranslationKeys.radioDrawLabel,
        ),
      },
    };
  },
);

export const css = withCompInfo<
  SignatureInputCSSVars,
  SignatureInputDefinition,
  SignatureInputCssCarmiData,
  { styleProperties: SignatureInputStyleProperties }
>()(
  ['compProps', 'compData', 'styleProperties', 'siteColors'],
  ({ compProps, compData, styleProperties, siteColors }, cssVars) => {
    const { titlePaddingStart, titleMarginBottom } = compProps;
    const {
      direction = 'inherit',
      labelDirection = 'inherit',
      inputDirection = 'inherit',
      buttonDirection = 'inherit',
    } = compData;
    const {
      align = 'start',
      labelAlign = 'start',
      buttonAlign = 'end',
    } = styleProperties;

    const signatureColor = getSignatureColorFromStyleProperties(
      styleProperties,
      siteColors,
    );

    const realLabelAlign = castFromInheritIfNeeded(labelAlign, align);
    const labelPaddigns =
      realLabelAlign === 'center'
        ? {
            '--labelPadding_start': '0',
            '--labelPadding_end': '0',
          }
        : ({
            [`--labelPadding_${realLabelAlign}`]: `${titlePaddingStart}px`,
            [`--labelPadding_${getOppositeAlign(realLabelAlign)}`]: '0',
          } as SignatureInputLabelPaddings);

    return {
      ...cssVars,
      ...labelPaddigns,
      '--direction': withSiteDirection(direction),
      '--align': align,
      '--labelDirection': labelDirection,
      '--labelAlign': labelAlign,
      '--inputDirection': inputDirection,
      '--buttonDirection': buttonDirection,
      '--buttonAlign': buttonAlign,
      '--labelMarginBottom': `${titleMarginBottom}px`,
      '--signatureColor': signatureColor,
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'compData',
        fields: ['direction'],
        enhancer: direction => castToInheritIfNeeded(direction, 'ltr'),
      },
    ]),
  ],
);

export const uiType = withUiTypeCompInfo<
  TypeOrDrawSignatureInputUiType,
  SignatureInputDefinition
>()(['skin'], ({ skin }) => {
  return isTypeOrDrawSkin(skin) ? skin : undefined;
});

export default createComponentMapperModel({ props, css, uiType });
